import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, FileUp } from "lucide-react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { ImageDrop, Thumb } from "./ImageUpload";
import { RebuildIssue } from "./RebuildIssue";

const EMPTY = { title: "", cover: "", pdf_url: "", pages: 0, size_mb: 0, show: true };

const PdfDrop = ({ f, setF }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/upload-pdf", body);
      setF((s) => ({ ...s, pdf_url: data.url, size_mb: data.size_mb }));
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="border-2 border-dashed border-ink p-5 bg-paper">
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="issue-pdf-input" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 kicker px-4 py-3 bg-ink text-cream hover:bg-hot hover:text-ink transition-colors disabled:opacity-60" data-testid="issue-pdf-upload-btn">
        <FileUp className="h-4 w-4" /> {busy ? "Uploading… (large files take a minute)" : f.pdf_url ? "Replace PDF" : "Upload PDF"}
      </button>
      {f.pdf_url && (
        <p className="text-sm mt-3" data-testid="issue-pdf-current">
          Current file: <a href={f.pdf_url} target="_blank" rel="noopener noreferrer" className="underline">open PDF</a> · {f.size_mb} MB
        </p>
      )}
    </div>
  );
};

export default function AdminIssue() {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/issue").then(({ data }) => setF({ ...EMPTY, ...(data ?? {}) })).catch(() => {});
    qc.invalidateQueries({ queryKey: ["/issue"] });
  }, [qc]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.pdf_url) return toast.error("Upload the PDF first");
    if (!f.cover) return toast.error("Add a cover image");
    setSaving(true);
    try {
      await api.put("/admin/issue", { ...f, pages: Number(f.pages) || 0 });
      qc.invalidateQueries({ queryKey: ["/issue"] });
      toast.success("Download section saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 sm:pt-32 pb-24" data-testid="admin-issue-page">
      <Link to="/admin" className="inline-flex items-center gap-2 kicker hover:text-rose" data-testid="admin-back-link"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      <h1 className="display text-6xl sm:text-7xl mt-6">Print issue</h1>
      <p className="font-serif italic text-xl text-ink/75 mt-2">The "Download the magazine" section on the home page. A 24-page flip-through preview is created automatically from the PDF a minute after saving.</p>
      <div className="mt-10"><RebuildIssue onDone={load} /></div>
      <h2 className="kicker mt-12">Or upload a PDF by hand</h2>
      <form onSubmit={save} className="mt-6 space-y-8">
        <label className="block">
          <span className="kicker">Title</span>
          <input value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} className="admin-input mt-2" placeholder="youth. Issue 27 — The parkour issue" data-testid="issue-title-input" />
        </label>
        <div>
          <span className="kicker">PDF file</span>
          <div className="mt-2"><PdfDrop f={f} setF={setF} /></div>
        </div>
        <div>
          <span className="kicker">Cover image</span>
          <div className="mt-2 max-w-[240px]">
            {f.cover ? (
              <Thumb src={f.cover} testId="issue-cover" onRemove={() => setF((s) => ({ ...s, cover: "" }))} />
            ) : (
              <ImageDrop label="Upload cover" testId="issue-cover-upload" onUploaded={(url) => setF((s) => ({ ...s, cover: url }))} />
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <label className="block">
            <span className="kicker">Pages</span>
            <input type="number" min="0" value={f.pages} onChange={(e) => setF((s) => ({ ...s, pages: e.target.value }))} className="admin-input mt-2" data-testid="issue-pages-input" />
          </label>
          <label className="flex items-center gap-3 sm:mt-8 cursor-pointer">
            <input type="checkbox" checked={f.show} onChange={(e) => setF((s) => ({ ...s, show: e.target.checked }))} className="h-5 w-5 accent-[#FF3D8B]" data-testid="issue-show-toggle" />
            <span>Show on the home page</span>
          </label>
        </div>
        <button disabled={saving} className="w-full bg-hot text-ink border-2 border-ink kicker py-4 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="issue-save-btn">
          {saving ? "Saving…" : "Save"}
        </button>
      </form>
    </main>
  );
}
